import type { ConversionError, ConversionResult } from '../services/enhanced-file-converter';

export type ExtractionLogSeverity = 'error' | 'warning' | 'info';

export interface ExtractionLogEntry {
  id: string;
  severity: ExtractionLogSeverity;
  category: string;
  message: string;
  detail?: string;
  section?: string;
  code?: string;
}

export interface ExtractionLogSummary {
  processedAt: string;
  fileName: string;
  fileType: string;
  fileSize: number;
  processingTimeMs: number;
  textLength: number;
  confidence: number;
  sectionsFound: string[];
  tableCount: number;
  counts: {
    errors: number;
    warnings: number;
    info: number;
    total: number;
  };
  entries: ExtractionLogEntry[];
}

const SECTION_LABELS: Record<string, string> = {
  patient: 'Demografía',
  patient_demographics: 'Demografía',
  ncs: 'NCS',
  ncs_data: 'NCS',
  emg: 'EMG',
  emg_data: 'EMG',
  diagnosis: 'Diagnóstico',
  conclusion: 'Conclusión',
  recommendations: 'Recomendaciones',
  clinical_notes: 'Notas clínicas',
};

const TABLE_KIND_LABELS: Record<string, string> = {
  motor_ncs: 'NCS motor',
  sensory_ncs: 'NCS sensitivo',
  needle_emg: 'EMG aguja',
  f_wave: 'Onda F',
  h_reflex: 'Reflejo H',
  blink_reflex: 'Reflejo de parpadeo',
  rns: 'Estimulación repetitiva',
  unknown: 'Tabla no clasificada',
};

const isTechnicalWarning = (warning: string): boolean =>
  /w:tblPrEx|unrecognised element|unrecognized element|mammoth/i.test(String(warning || ''));

const mapErrorSeverity = (severity: ConversionError['severity']): ExtractionLogSeverity => {
  if (severity === 'critical' || severity === 'high') return 'error';
  if (severity === 'medium') return 'warning';
  return 'info';
};

let entryCounter = 0;
const nextId = (prefix: string) => `${prefix}-${++entryCounter}`;

/**
 * Construye un log unificado de extracción a partir del resultado del convertidor.
 * Incluye errores de validación, advertencias clínicas/técnicas, tablas clasificadas
 * y puntos de revisión manual.
 */
export function buildExtractionLog(
  processingResult: ConversionResult,
  file: File,
  extras?: { fallbackNarrative?: boolean; extraWarnings?: string[] }
): ExtractionLogSummary {
  entryCounter = 0;
  const entries: ExtractionLogEntry[] = [];
  const metadata = processingResult.metadata;
  const summary = metadata?.extractionSummary;

  entries.push({
    id: nextId('proc'),
    severity: 'info',
    category: 'Proceso',
    message: `Archivo procesado: ${file.name}`,
    detail: `${(file.size / 1024).toFixed(1)} KB · ${metadata?.fileType || file.type || 'desconocido'}`,
  });

  if (metadata?.processingTimeMs != null) {
    entries.push({
      id: nextId('proc'),
      severity: 'info',
      category: 'Proceso',
      message: `Tiempo de procesamiento: ${metadata.processingTimeMs} ms`,
    });
  }

  if (metadata?.extractedTextLength != null) {
    entries.push({
      id: nextId('proc'),
      severity: 'info',
      category: 'Proceso',
      message: `Texto extraído: ${metadata.extractedTextLength.toLocaleString()} caracteres`,
    });
  }

  entries.push({
    id: nextId('proc'),
    severity: processingResult.confidence >= 0.6 ? 'info' : 'warning',
    category: 'Proceso',
    message: `Confianza global: ${(processingResult.confidence * 100).toFixed(1)}%`,
  });

  (metadata?.sectionsFound || []).forEach(section => {
    entries.push({
      id: nextId('sec'),
      severity: 'info',
      category: 'Secciones',
      message: `Sección detectada: ${SECTION_LABELS[section] || section}`,
      section,
    });
  });

  if (metadata?.structuredExtraction) {
    const se = metadata.structuredExtraction;
    entries.push({
      id: nextId('struct'),
      severity: 'info',
      category: 'Estructura',
      message: `Formato estructurado: ${se.sourceFormat}`,
      detail: `${se.tableCount} tabla(s) · ${se.pageCount} página(s)`,
    });
    se.warnings.forEach(warning => {
      entries.push({
        id: nextId('struct-w'),
        severity: isTechnicalWarning(warning) ? 'info' : 'warning',
        category: isTechnicalWarning(warning) ? 'Técnico' : 'Estructura',
        message: warning,
      });
    });
  }

  (summary?.classifiedTables || []).forEach(table => {
    const kindLabel = TABLE_KIND_LABELS[table.kind] || table.kind;
    entries.push({
      id: nextId('tbl'),
      severity: table.confidence >= 0.6 ? 'info' : 'warning',
      category: 'Tablas',
      message: `Tabla ${table.tableIndex + 1}: ${kindLabel}`,
      detail: `${table.rows} fila(s) · confianza ${(table.confidence * 100).toFixed(0)}%`,
    });
  });

  (processingResult.errors || []).forEach(error => {
    entries.push({
      id: nextId('err'),
      severity: mapErrorSeverity(error.severity),
      category: 'Validación',
      message: error.message,
      detail: error.suggestion,
      section: error.section ? SECTION_LABELS[error.section] || error.section : undefined,
      code: error.code,
    });
  });

  const allWarnings = [
    ...(processingResult.warnings || []),
    ...(summary?.warnings || []),
    ...(extras?.extraWarnings || []),
  ];

  allWarnings.forEach(warning => {
    const technical = isTechnicalWarning(warning);
    entries.push({
      id: nextId('warn'),
      severity: technical ? 'info' : 'warning',
      category: technical ? 'Técnico' : 'Extracción',
      message: warning,
    });
  });

  (summary?.reviewItems || []).forEach(item => {
    entries.push({
      id: nextId('rev'),
      severity: 'warning',
      category: 'Trazabilidad',
      message: item,
    });
  });

  if (extras?.fallbackNarrative) {
    entries.push({
      id: nextId('fb'),
      severity: 'warning',
      category: 'Respaldo',
      message: 'Se usó extracción narrativa como fallback porque no se detectaron tablas NCS/EMG.',
    });
  }

  if (summary) {
    if (!summary.patientDetected) {
      entries.push({
        id: nextId('rev'),
        severity: 'warning',
        category: 'Trazabilidad',
        message: 'No se detectaron datos demográficos estructurados en el documento.',
      });
    }
    entries.push({
      id: nextId('cnt'),
      severity: 'info',
      category: 'Resumen',
      message: `Registros extraídos: ${summary.ncsCount} NCS · ${summary.emgCount} EMG · ${summary.specialStudiesCount} estudios especiales`,
    });
  }

  const counts = {
    errors: entries.filter(e => e.severity === 'error').length,
    warnings: entries.filter(e => e.severity === 'warning').length,
    info: entries.filter(e => e.severity === 'info').length,
    total: entries.length,
  };

  return {
    processedAt: metadata?.processedAt || new Date().toISOString(),
    fileName: file.name,
    fileType: metadata?.fileType || file.type,
    fileSize: file.size,
    processingTimeMs: metadata?.processingTimeMs || 0,
    textLength: metadata?.extractedTextLength || 0,
    confidence: processingResult.confidence,
    sectionsFound: metadata?.sectionsFound || [],
    tableCount: summary?.classifiedTables?.length || metadata?.structuredExtraction?.tableCount || 0,
    counts,
    entries,
  };
}

/** Log simplificado para rutas alternativas (parser robusto, fallback manual). */
export function buildSimpleExtractionLog(
  file: File,
  options: {
    confidence: number;
    source: string;
    warnings?: string[];
    infoMessages?: string[];
    errorMessages?: string[];
    processingTimeMs?: number;
  }
): ExtractionLogSummary {
  entryCounter = 0;
  const entries: ExtractionLogEntry[] = [
    {
      id: nextId('src'),
      severity: 'info',
      category: 'Proceso',
      message: `Extracción vía ${options.source}`,
      detail: file.name,
    },
    {
      id: nextId('conf'),
      severity: options.confidence >= 0.6 ? 'info' : 'warning',
      category: 'Proceso',
      message: `Confianza: ${(options.confidence * 100).toFixed(1)}%`,
    },
    ...(options.infoMessages || []).map(msg => ({
      id: nextId('info'),
      severity: 'info' as ExtractionLogSeverity,
      category: 'Resumen',
      message: msg,
    })),
    ...(options.errorMessages || []).map(msg => ({
      id: nextId('err'),
      severity: 'error' as ExtractionLogSeverity,
      category: 'Validación',
      message: msg,
    })),
    ...(options.warnings || []).map(msg => ({
      id: nextId('warn'),
      severity: (isTechnicalWarning(msg) ? 'info' : 'warning') as ExtractionLogSeverity,
      category: isTechnicalWarning(msg) ? 'Técnico' : 'Extracción',
      message: msg,
    })),
  ];

  return {
    processedAt: new Date().toISOString(),
    fileName: file.name,
    fileType: file.type,
    fileSize: file.size,
    processingTimeMs: options.processingTimeMs || 0,
    textLength: 0,
    confidence: options.confidence,
    sectionsFound: [],
    tableCount: 0,
    counts: {
      errors: entries.filter(e => e.severity === 'error').length,
      warnings: entries.filter(e => e.severity === 'warning').length,
      info: entries.filter(e => e.severity === 'info').length,
      total: entries.length,
    },
    entries,
  };
}

/** Log para cuando falla el procesamiento automático y se usa una ruta de respaldo. */
export function buildFallbackExtractionLog(
  file: File,
  errorMessage: string,
  extraWarnings: string[] = []
): ExtractionLogSummary {
  entryCounter = 0;
  const entries: ExtractionLogEntry[] = [
    {
      id: nextId('fail'),
      severity: 'error',
      category: 'Proceso',
      message: 'Falló el procesamiento automático del archivo',
      detail: errorMessage,
    },
    ...extraWarnings.map(warning => ({
      id: nextId('warn'),
      severity: (isTechnicalWarning(warning) ? 'info' : 'warning') as ExtractionLogSeverity,
      category: isTechnicalWarning(warning) ? 'Técnico' : 'Respaldo',
      message: warning,
    })),
  ];

  return {
    processedAt: new Date().toISOString(),
    fileName: file.name,
    fileType: file.type,
    fileSize: file.size,
    processingTimeMs: 0,
    textLength: 0,
    confidence: 0,
    sectionsFound: [],
    tableCount: 0,
    counts: {
      errors: entries.filter(e => e.severity === 'error').length,
      warnings: entries.filter(e => e.severity === 'warning').length,
      info: entries.filter(e => e.severity === 'info').length,
      total: entries.length,
    },
    entries,
  };
}

/** Serializa el log para copiar al portapapeles o guardar en logserrores/. */
export function serializeExtractionLog(log: ExtractionLogSummary): string {
  return JSON.stringify(log, null, 2);
}
